import { Modal } from "antd";

// Render-time estimates (mid-range laptop): ≤50→5–10s, ≤200→~30s,
// ≤500→1–2m, ≤1000→2–3m, >1000→up to 5m. Intentionally conservative.
type Guidance = {
  title: string;
  eta: string;
  hint: string;
  severity: "info" | "warn";
};

function describeRenderTime(rowCount: number): Guidance {
  if (rowCount <= 50) {
    return {
      title: "Opening print view",
      eta: "This should take about 5–10 seconds.",
      hint: "The print dialog will open automatically once the page is ready.",
      severity: "info"
    };
  }
  if (rowCount <= 200) {
    return {
      title: "Preparing print view",
      eta: `Rendering ${rowCount} rows — this typically takes under a minute.`,
      hint: "Please keep the new tab open. The print dialog will open automatically once the page is ready.",
      severity: "info"
    };
  }
  if (rowCount <= 500) {
    return {
      title: "Preparing a large print view",
      eta: `Rendering ${rowCount} rows — this usually takes 1–2 minutes.`,
      hint: "Please keep the new tab open and avoid switching tabs while it renders. Diagrams are converted to images to keep the browser responsive. The print dialog will open automatically once everything is ready.",
      severity: "warn"
    };
  }
  // Only caller (launchPrintView) early-returns when rowCount > FILTER_FIRST_THRESHOLD (1000),
  // so describeRenderTime is never invoked with rowCount > 1000: this guard is always true
  // and the very-large-view fall-through below is unreachable in practice.
  /* v8 ignore else */
  if (rowCount <= 1000) {
    return {
      title: "Preparing a large print view",
      eta: `Rendering ${rowCount} rows — this can take 2–3 minutes.`,
      hint: "Please keep the new tab open. Diagrams are converted to images to keep the browser responsive. The print dialog will open automatically once everything is ready.",
      severity: "warn"
    };
  }
  // Unreachable: rowCount > 1000 is handled by launchAsFilterFirst before this function runs.
  /* v8 ignore next 6 */
  return {
    title: "Preparing a very large print view",
    eta: `Rendering ${rowCount} rows — this may take up to 5 minutes.`,
    hint: "Please keep the new tab open and don't interact with it. If the browser warns that the page is unresponsive, choose Wait. The print dialog will open automatically once rendering completes.",
    severity: "warn"
  };
}

// Above this row count the primary action flips to "Filter first";
// open is still possible via the secondary button.
const FILTER_FIRST_THRESHOLD = 1000;

// `openFn` MUST be called inside the modal's onOk/onCancel handler so the
// trusted click propagates to window.open (popup-blocker requirement).
export function launchPrintView(openFn: () => void, rowCount: number): void {
  if (rowCount === 0) return;

  if (rowCount > FILTER_FIRST_THRESHOLD) {
    launchAsFilterFirst(openFn, rowCount);
    return;
  }

  const { title, eta, hint } = describeRenderTime(rowCount);
  Modal.confirm({
    title,
    icon: null,
    content: (
      <div className="text-sm space-y-2 pt-2">
        <div className="text-gray-900">{eta}</div>
        <div className="text-gray-500 leading-relaxed">{hint}</div>
      </div>
    ),
    okText: "Open Print View",
    cancelText: "Cancel",
    width: 480,
    onOk: () => openFn()
  });
}

// Wires "Open anyway" to onCancel + "Filter first" to onOk so AntD
// renders "Filter first" as the primary button (its Modal.confirm
// styling follows ok/cancel role, not text).
function launchAsFilterFirst(openFn: () => void, rowCount: number): void {
  Modal.confirm({
    title: `${rowCount} rows is a lot of paper`,
    icon: null,
    width: 520,
    content: (
      <div className="text-sm space-y-2 pt-2">
        <div className="text-gray-900">
          Printing all {rowCount} rows would produce hundreds of pages and can
          take 5+ minutes to prepare. Most of the time the report is more useful
          when scoped to a specific risk level, spec, or contractor.
        </div>
        <div className="text-gray-500 leading-relaxed">
          Close this dialog, apply a filter on the grid (risk category, column
          filters, or the search box), then open the print view again — the
          preparation time drops dramatically.
        </div>
      </div>
    ),
    okText: "Filter first",
    okButtonProps: { type: "primary" },
    cancelText: "Open anyway (slow)",
    onCancel: () => openFn()
  });
}
